// react
import { useEffect, useRef } from 'react';
import type { Step } from 'react-joyride';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';

// api
import type { UnitsGetCollectionAsyncByqueryOptionsApiArg } from '@/api/generated/recalc/endpoints/v1/units';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { WritableOnly } from '@/components/access-control';
import { getUnitOverviewTour } from '@/components/joyride/guided-tours/UnitOverview.tour';
import useAppTour from '@/components/joyride/useAppTour';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { CreateButtonLink } from '@/components/ui/button';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import UnitDataGrid from '@/features/units/components/UnitDataGrid';

import { skipToken } from '@reduxjs/toolkit/query';

// components

export const Route = createFileRoute('/$accountId/_user/units/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const search = useSearch({ strict: false });
  const hasAutoStartedTourRef = useRef(false);
  const { accountId } = useParams({ strict: false });
  const { controls, Tour } = useAppTour({
    steps: getUnitOverviewTour() as Step[],
  });

  useEffect(() => {
    const isUnitOverviewTour =
      search?.tour === 'units-overview' || search?.tour === 'units';

    if (isUnitOverviewTour && !hasAutoStartedTourRef.current) {
      hasAutoStartedTourRef.current = true;
      controls.start();
    } else if (!search?.tour) {
      controls.close();
    }
  }, [controls, search?.tour]);

  const queryArg:
    | UnitsGetCollectionAsyncByqueryOptionsApiArg
    | typeof skipToken = accountId
      ? { 'Account.AccountId': `eq(${accountId})` }
      : skipToken;

  const {
    data: units,
    isLoading,
    isFetching,
  } = useUnitsGetCollectionAsyncByqueryOptionsQuery(queryArg);

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading || isFetching} />
      <PageContainer
        maxWidth="xl"
        sx={{ py: 2 }}
        data-testid="unit-overview-container"
      >
        {Tour}
        <PageHeader title={t('units.unitOverview')} disableArrow={true}>
          <WritableOnly>
            <CreateButtonLink
              to="create"
              data-tour="unit-overview-add"
              data-testid="unit-add-button"
            >
              {t('units.newUnit')}
            </CreateButtonLink>
          </WritableOnly>
        </PageHeader>
        <UnitDataGrid units={units} isLoading={isLoading} />
      </PageContainer>
    </>
  );
}
